import { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertTriangleIcon, InfoIcon, RefreshCwIcon } from "lucide-react";
import { InfoHint } from "@dolfin/ui";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatNumber } from "@/lib/format";
import { MatchCountByToggle } from "./MatchCountByToggle";
import {
  AUTOMATIC_BUCKETS,
  MATCH_OUTCOME_GROUPS,
  MATCH_VIZ_CLASS,
  MATCH_VIZ_STYLES,
  toChartNumber,
  type MatchOutcomeGroupKey,
} from "./matchOutcomeBuckets";
import { useAnalyticsParams, type MatchUnit } from "./useAnalyticsParams";
import { chartTooltipProps } from "@/components/charts/ChartTooltip";

type MatchOutcomesDailyResponse = components["schemas"]["MatchOutcomesDailyResponse"];
type MatchOutcomeDailyRow = components["schemas"]["MatchOutcomeDailyRow"];

const ENDPOINT = "/admin/analytics/match-outcomes-daily";

/** Under this many matches, a percentage says more than the data supports, so the sentence uses counts. */
const MIN_EVENTS_FOR_PERCENTAGES = 10;

function buildQuery(apiParams: URLSearchParams, unit: MatchUnit) {
  return {
    From: apiParams.get("From") ?? "",
    To: apiParams.get("To") ?? "",
    ClientIds: apiParams.getAll("ClientIds"),
    OrganisationId: apiParams.get("OrganisationId") || undefined,
    Unit: unit,
  };
}

/** "2026-09-08" -> "8 Sep". Parsed by hand so a date-only string is never shifted by the local timezone. */
function dateLabel(dateStr: string): string {
  const [year, month, day] = dateStr.split("-").map(Number);
  if (!year || !month || !day) return dateStr;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/** The three headline categories, as counts. Keyed the way the chart's stacked series are keyed. */
export type GroupTotals = Record<MatchOutcomeGroupKey, number>;

function emptyTotals(): GroupTotals {
  return { Automatic: 0, Confirmed: 0, Manual: 0 };
}

/** The five recorded methods on one row, rolled up into the three categories the default view shows. */
function rowTotals(row: MatchOutcomeDailyRow): GroupTotals {
  return {
    Automatic:
      toChartNumber(row.autoSku) + toChartNumber(row.autoSimilarity) + toChartNumber(row.autoLinkedPo),
    Confirmed: toChartNumber(row.confirmed),
    Manual: toChartNumber(row.manual),
  };
}

function addTotals(target: GroupTotals, source: GroupTotals): void {
  target.Automatic += source.Automatic;
  target.Confirmed += source.Confirmed;
  target.Manual += source.Manual;
}

function sumTotals(totals: GroupTotals): number {
  return totals.Automatic + totals.Confirmed + totals.Manual;
}

/** Whole percentages summing to exactly 100 (largest remainder); zeroes for an empty total. */
export function sharesOf(totals: GroupTotals): GroupTotals {
  const total = sumTotals(totals);
  if (total === 0) return emptyTotals();
  const keys: MatchOutcomeGroupKey[] = ["Automatic", "Confirmed", "Manual"];
  const exact = keys.map((key) => ({ key, value: (totals[key] / total) * 100 }));
  const floored = exact.map((entry) => ({ ...entry, whole: Math.floor(entry.value) }));
  let remaining = 100 - floored.reduce((sum, entry) => sum + entry.whole, 0);
  const byRemainder = [...floored].sort(
    (a, b) => b.value - b.whole - (a.value - a.whole) || a.key.localeCompare(b.key),
  );
  const result = emptyTotals();
  for (const entry of floored) result[entry.key] = entry.whole;
  for (const entry of byRemainder) {
    if (remaining <= 0) break;
    result[entry.key] += 1;
    remaining -= 1;
  }
  return result;
}

function plural(count: number, singular: string, pluralForm: string): string {
  return count === 1 ? singular : pluralForm;
}

/** The headline sentence: percentages once there's enough data, plain counts below that. */
export function outcomesHeadline(totals: GroupTotals): string {
  const total = sumTotals(totals);
  if (total === 0) return "No matches in this window.";
  if (total < MIN_EVENTS_FOR_PERCENTAGES) {
    return `Only ${formatNumber(total)} ${plural(total, "match", "matches")} in this window, which is too few for a percentage. ${formatNumber(totals.Automatic)} matched automatically, ${formatNumber(totals.Confirmed)} ${plural(totals.Confirmed, "was an approved suggestion", "were approved suggestions")}, ${formatNumber(totals.Manual)} matched manually.`;
  }
  const shares = sharesOf(totals);
  return `${shares.Automatic}% matched automatically, ${shares.Confirmed}% were approved suggestions, ${shares.Manual}% were matched manually.`;
}

interface DailyPoint extends GroupTotals {
  date: string;
  label: string;
  total: number;
}

interface OrgSummary {
  id: string;
  name: string;
  totals: GroupTotals;
  total: number;
}

export interface MatchOutcomesDailyChartProps {
  apiParams: URLSearchParams;
  unit: MatchUnit;
}

/** Section 2: how matches were made per day, five recorded methods rolled up to three. All organisations in
 * scope are counted together in one chart. */
export function MatchOutcomesDailyChart({ apiParams, unit }: MatchOutcomesDailyChartProps) {
  const apiParamsKey = apiParams.toString();
  const { setParam } = useAnalyticsParams();
  const [data, setData] = useState<MatchOutcomesDailyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      const { data: response, error: apiError } = await api.GET(ENDPOINT, {
        params: { query: buildQuery(apiParams, unit) },
      });
      if (cancelled) return;
      if (apiError || !response) {
        setError("Daily match outcomes could not be loaded.");
      } else {
        setData(response);
      }
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiParamsKey, unit, refreshKey]);

  const rows = useMemo(() => data?.rows ?? [], [data]);

  /** One column per day, every organisation in scope counted together. */
  const points = useMemo<DailyPoint[]>(() => {
    const byDate = new Map<string, DailyPoint>();
    for (const row of rows) {
      let point = byDate.get(row.date);
      if (!point) {
        point = { date: row.date, label: dateLabel(row.date), ...emptyTotals(), total: 0 };
        byDate.set(row.date, point);
      }
      const totals = rowTotals(row);
      addTotals(point, totals);
      point.total += sumTotals(totals);
    }
    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [rows]);

  const windowTotals = useMemo(() => {
    const totals = emptyTotals();
    for (const row of rows) addTotals(totals, rowTotals(row));
    return totals;
  }, [rows]);
  const windowTotal = sumTotals(windowTotals);

  /** The five-way split, for Data details. */
  const methodTotals = useMemo(() => {
    const totals = { AutoSku: 0, AutoSimilarity: 0, AutoLinkedPo: 0 };
    for (const row of rows) {
      totals.AutoSku += toChartNumber(row.autoSku);
      totals.AutoSimilarity += toChartNumber(row.autoSimilarity);
      totals.AutoLinkedPo += toChartNumber(row.autoLinkedPo);
    }
    return totals;
  }, [rows]);

  const byOrg = useMemo<OrgSummary[]>(() => {
    const map = new Map<string, OrgSummary>();
    for (const row of rows) {
      let entry = map.get(row.organisationId);
      if (!entry) {
        entry = { id: row.organisationId, name: row.organisationName, totals: emptyTotals(), total: 0 };
        map.set(row.organisationId, entry);
      }
      const totals = rowTotals(row);
      addTotals(entry.totals, totals);
      entry.total += sumTotals(totals);
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [rows]);

  const inconsistentRows = data ? toChartNumber(data.inconsistentRows) : 0;

  if (loading && !data) {
    return (
      <div className="flex flex-col gap-3">
        <div className="bg-muted h-6 w-2/3 animate-pulse rounded" />
        <div className="bg-muted h-64 animate-pulse rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="border-destructive/30 bg-destructive/5 flex flex-col items-center gap-3 rounded-lg border px-6 py-10 text-center">
        <AlertTriangleIcon className="text-destructive size-6" />
        <p className="text-sm font-medium">Could not load daily match outcomes</p>
        <p className="text-muted-foreground max-w-md text-sm">{error}</p>
        <button
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          className="border-border hover:bg-accent mt-1 inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition"
        >
          <RefreshCwIcon className="size-3.5" />
          Try again
        </button>
      </div>
    );
  }

  if (windowTotal === 0) {
    return (
      <div className="border-border flex flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-12 text-center">
        <InfoIcon className="text-muted-foreground size-6" />
        <p className="text-sm font-medium">No matches in this window</p>
        <p className="text-muted-foreground max-w-md text-sm">
          Widen the date range, or check that the selected client has matching activity.
        </p>
      </div>
    );
  }

  const shares = sharesOf(windowTotals);
  const automaticTotal = windowTotals.Automatic;

  return (
    <div className={`${MATCH_VIZ_CLASS} flex flex-col gap-5`}>
      <style>{MATCH_VIZ_STYLES}</style>

      <div>
        <p className="text-lg leading-snug font-medium">{outcomesHeadline(windowTotals)}</p>
        <p className="text-muted-foreground mt-1 text-sm">
          {formatNumber(windowTotal)} {plural(windowTotal, "match", "matches")} in the selected range
          {byOrg.length > 1 ? `, across ${formatNumber(byOrg.length)} organisations counted together` : ""}.
        </p>
      </div>

      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={points} margin={{ top: 4, right: 4, bottom: 0, left: -18 }} barCategoryGap="14%">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border, #e5e7eb)" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10 }}
              stroke="var(--color-muted-foreground, #6b7280)"
              interval="preserveStartEnd"
              minTickGap={24}
            />
            <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground, #6b7280)" allowDecimals={false} />
            <Tooltip
              {...chartTooltipProps}
            />
            {/* Every category keeps its legend entry at zero or not, so a reader learns the three exist. */}
            <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
            {MATCH_OUTCOME_GROUPS.map((group) => (
              <Bar
                key={group.key}
                dataKey={group.key}
                name={group.label}
                stackId="day"
                fill={group.color}
                stroke="var(--viz-surface)"
                strokeWidth={2}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {byOrg.length > 1 && (
        <p className="text-muted-foreground text-xs">
          All organisations are counted together here. Pick one in the filter above, or see the
          organisation by organisation comparison under Data details.
        </p>
      )}

      <Accordion type="single" collapsible className="border-t">
        <AccordionItem value="details" className="border-b-0">
          <AccordionTrigger>Data details</AccordionTrigger>
          <AccordionContent className="space-y-6 text-sm">
            <MatchCountByToggle unit={unit} onChange={(next) => setParam("unit", next)} />

            <div>
              <div className="flex items-center gap-1.5">
                <p className="font-medium">How the automatic matches were made</p>
                <InfoHint label="How the automatic matches were made">
                  The three ways the system matches a line on its own. The headline chart adds these
                  together, because the difference between them is an engineering question, not a
                  decision anyone makes from this page.
                </InfoHint>
              </div>
              <div className="mt-2 rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Method</TableHead>
                      <TableHead className="text-right">Matches</TableHead>
                      <TableHead className="text-right">Share of automatic</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {AUTOMATIC_BUCKETS.map((bucket) => {
                      const count = methodTotals[bucket.key as keyof typeof methodTotals] ?? 0;
                      return (
                        <TableRow key={bucket.key}>
                          <TableCell>{bucket.label}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatNumber(count)}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {automaticTotal === 0 ? "-" : `${Math.round((count / automaticTotal) * 100)}%`}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div>
              <p className="font-medium">Day by day</p>
              <div className="mt-2 max-h-96 overflow-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      {MATCH_OUTCOME_GROUPS.map((group) => (
                        <TableHead key={group.key} className="text-right">
                          {group.label}
                        </TableHead>
                      ))}
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {points.map((point) => (
                      <TableRow key={point.date}>
                        <TableCell className="whitespace-nowrap">{point.label}</TableCell>
                        {MATCH_OUTCOME_GROUPS.map((group) => (
                          <TableCell key={group.key} className="text-right tabular-nums">
                            {formatNumber(point[group.key])}
                          </TableCell>
                        ))}
                        <TableCell className="text-right tabular-nums">{formatNumber(point.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div>
              <p className="font-medium">Organisation by organisation</p>
              <div className="mt-2 max-h-96 overflow-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Organisation</TableHead>
                      {MATCH_OUTCOME_GROUPS.map((group) => (
                        <TableHead key={group.key} className="text-right">
                          {group.label}
                        </TableHead>
                      ))}
                      <TableHead className="text-right">Automatic share</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byOrg.map((org) => (
                      <TableRow key={org.id}>
                        <TableCell className="max-w-48 truncate">{org.name}</TableCell>
                        {MATCH_OUTCOME_GROUPS.map((group) => (
                          <TableCell key={group.key} className="text-right tabular-nums">
                            {formatNumber(org.totals[group.key])}
                          </TableCell>
                        ))}
                        <TableCell className="text-right tabular-nums">
                          {org.total === 0 ? "-" : `${Math.round((org.totals.Automatic / org.total) * 100)}%`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {inconsistentRows > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                <AlertTriangleIcon className="mt-0.5 size-3.5 shrink-0" />
                <span>
                  Possible counting issue: {formatNumber(inconsistentRows)} matched{" "}
                  {plural(inconsistentRows, "line is", "lines are")} recorded as both matched manually and
                  matched automatically. That should not happen, so the automatic figures may be too high.
                </span>
              </div>
            )}

            <p className="text-muted-foreground text-xs">
              Percentages in the sentence above are rounded to whole numbers and adjusted so they add up to
              100.
            </p>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
